import CashflowAssistantApi from '@/api/CashflowAssistant';
import DDLogs from '@/DDLogs';

// The server is the source of truth (`hasUnreadCx` per conversation), so the
// badge survives a reload and shows for replies that arrived while the customer
// was logged out. Socket pushes only keep it live within a session.
const state = {
  hasUnread: false,
  // Which conversation the unread reply is in, so a client that was away when
  // it arrived can surface it rather than only showing a dot.
  unreadConversationId: null,
  unreadConversationIds: [],
  // The conversation the open chat is showing, so a push about the thread the
  // customer is already reading does not raise a badge.
  openConversationId: null,
  // Whether the chat sheet is mounted at all; openConversationId is null both
  // when the sheet is closed and when it is open on a not-yet-saved conversation.
  chatOpen: false,
  initialised: false,
};

const getters = {
  hasUnreadCxMessage: state => state.hasUnread,
  hasUnreadInOtherConversation: state => state.unreadConversationIds.some(id => id !== state.openConversationId),
};

const actions = {
  // Once per session: the badge has to be correct on first paint.
  async initUnread({ state, commit, dispatch }) {
    if (state.initialised) {
      return;
    }
    commit('setInitialised', true);
    await dispatch('refreshUnread');
  },

  // Re-derives unread state from the server. The push path dies silently when
  // the socket exhausts its reconnection attempts (a websockets deploy is
  // enough), so anything that notices the tab waking up calls this.
  async refreshUnread({ commit }) {
    try {
      const { conversations } = await CashflowAssistantApi.getConversations();
      commit('setUnread', conversations || []);
    } catch (error) {
      DDLogs.error('[inline-cx] Failed to load unread chat state', { error });
    }
  },

  // Clears server-side too, so the badge does not return on the next load. The
  // flag is recomputed from the list afterwards, because another conversation
  // may still hold an unread reply.
  async markRead({ commit }, conversationId) {
    commit('setHasUnread', false);
    if (!conversationId) {
      return;
    }
    try {
      await CashflowAssistantApi.markConversationRead(conversationId);
      const { conversations } = await CashflowAssistantApi.getConversations();
      commit('setUnread', conversations || []);
    } catch (error) {
      DDLogs.error('[inline-cx] Failed to mark conversation read', { error });
    }
  },
};

const mutations = {
  setHasUnread(state, hasUnread) {
    state.hasUnread = hasUnread;
    if (!hasUnread) { state.unreadConversationId = null; }
  },
  setUnread(state, conversations) {
    const unread = conversations.filter(c => c.hasUnreadCx);
    state.hasUnread = unread.length > 0;
    // The conversations route does not camel-case its payload — it only adds
    // hasUnreadCx — so the id arrives snake_cased. Both are read because the
    // rest of the chat API does convert.
    const ids = unread.map(c => c.conversation_id || c.conversationId);
    state.unreadConversationIds = ids;
    state.unreadConversationId = ids[0] || null;
  },
  setOpenConversationId(state, conversationId) {
    state.openConversationId = conversationId;
  },
  setChatOpen(state, open) {
    state.chatOpen = open;
  },
  setInitialised(state, initialised) {
    state.initialised = initialised;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
